/**
 * Deterministic simulation for Holly's deliberately small companion contract.
 *
 * It knows authored points and volumes, never scene objects. That keeps the
 * decisions which can change a puzzle or a detection replayable in fixed time;
 * animation, audio and meshes can only read the resulting snapshot.
 */

/** A ground-plane point, kept independent of Three and Rapier. */
export interface Vec2 {
  readonly x: number;
  readonly z: number;
}

/** A doorway footprint used to keep Holly from blocking Rosalie's passage. */
export interface CompanionDoorway {
  readonly id: string;
  readonly min: Vec2;
  readonly max: Vec2;
}

/** The real, uninflated ground footprint of one solid Holly must walk around. */
export interface CompanionObstacle {
  readonly id: string;
  readonly min: Vec2;
  readonly max: Vec2;
}

/** An authored target Holly uses when a room requests a contextual action. */
export interface CompanionStation {
  readonly id: string;
  readonly target: Vec2;
}

export type CompanionPhase =
  | 'trapped'
  | 'released'
  | 'following'
  | 'at-high-latch'
  | 'opening-door'
  | 'resting';

/** All mutable simulation facts which presentation and later bell code may read. */
export interface CompanionSnapshot {
  readonly x: number;
  readonly z: number;
  /** Radians: 0 faces +Z, increasing toward +X, matching worker/stealth. */
  readonly yaw: number;
  readonly moving: boolean;
  readonly phase: CompanionPhase;
  /** Grounded metres travelled since construction/reset; later bell code consumes this. */
  readonly travelledDistance: number;
}

/** The authored inputs for one companion instance; no global room values live here. */
export interface CompanionConfig {
  readonly initial: Omit<CompanionSnapshot, 'moving' | 'travelledDistance'> & {
    readonly moving?: boolean;
    readonly travelledDistance?: number;
  };
  readonly standoffRadius: number;
  readonly moveSpeed: number;
  /** Per-second convergence rate once close enough that max speed no longer governs. */
  readonly smoothing: number;
  /** Distance from a selected target at which Holly comes to rest. */
  readonly arrivalRadius: number;
  readonly doorways: readonly CompanionDoorway[];
  readonly stations: readonly CompanionStation[];
  readonly bodyRadius: number;
  readonly obstacles: readonly CompanionObstacle[];
}

/** Per-step facts supplied by the room without exposing stealth or scene state. */
export interface CompanionContext {
  /** Ignore standoff and seek the nearest allowed authored station this step. */
  readonly atStation: boolean;
  /** Omit for all authored stations; otherwise only these ids are usable this step. */
  readonly stationIds?: readonly string[];
  /** Omit for all authored obstacles; otherwise only these ids are solid this step. */
  readonly obstacleIds?: readonly string[];
}

interface MutableState {
  x: number;
  z: number;
  yaw: number;
  moving: boolean;
  phase: CompanionPhase;
  travelledDistance: number;
}

/**
 * The slack on every distance comparison in this file, metres.
 *
 * **Exported because it is part of the arrival contract, not an implementation
 * detail.** `#moveToward` halts at `distance <= arrivalRadius + EPSILON`, so a
 * gate that asserts she reached a station has to allow the same slack — and
 * `beat-10-call.spec.ts` asserted a bare `<= ARRIVAL_RADIUS` and measured
 * 0.20000000000000057 the day beat 10's gap route changed her approach angle.
 * That is a float-width miss against a bound this module never promised, and
 * naming the bound is the fix; loosening the assertion by an invented margin is
 * not.
 */
export const COMPANION_EPSILON = 0.000000001;
const EPSILON = COMPANION_EPSILON;
/** Technical separation only: `inside()` includes authored boundaries. */
const DOORWAY_CLEARANCE = 0.000001;
/** Inclusive footprints need a real exit, not a point left on their boundary. */
const OBSTACLE_CLEARANCE = 0.000001;

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`companion ${label} must be finite`);
}

function positive(value: number, label: string): void {
  finite(value, label);
  if (value <= 0) throw new Error(`companion ${label} must be positive`);
}

function nonNegative(value: number, label: string): void {
  finite(value, label);
  if (value < 0) throw new Error(`companion ${label} must not be negative`);
}

function normaliseAngle(angle: number): number {
  let normalised = (angle + Math.PI) % (Math.PI * 2);
  if (normalised <= 0) normalised += Math.PI * 2;
  return normalised - Math.PI;
}

function inside(point: Vec2, volume: { readonly min: Vec2; readonly max: Vec2 }): boolean {
  return point.x >= volume.min.x && point.x <= volume.max.x
    && point.z >= volume.min.z && point.z <= volume.max.z;
}

function sameSnapshot(state: MutableState): CompanionSnapshot {
  return {
    x: state.x,
    z: state.z,
    yaw: state.yaw,
    moving: state.moving,
    phase: state.phase,
    travelledDistance: state.travelledDistance,
  };
}

/**
 * Fixed-step companion controller. It knows authored points and volumes only;
 * geometry arrives as data derived from the room's one solid list. Phase changes are deliberately explicit:
 * allowing `step()` or a presentation callback to promote the puzzle would let
 * Holly solve it while Rosalie is elsewhere.
 */
export class Companion {
  readonly #config: CompanionConfig;
  readonly #initial: MutableState;
  readonly #state: MutableState;

  constructor(config: CompanionConfig) {
    finite(config.initial.x, 'initial.x');
    finite(config.initial.z, 'initial.z');
    finite(config.initial.yaw, 'initial.yaw');
    nonNegative(config.initial.travelledDistance ?? 0, 'initial.travelledDistance');
    positive(config.standoffRadius, 'standoffRadius');
    positive(config.moveSpeed, 'moveSpeed');
    positive(config.smoothing, 'smoothing');
    nonNegative(config.arrivalRadius, 'arrivalRadius');
    positive(config.bodyRadius, 'bodyRadius');
    if (config.arrivalRadius >= config.standoffRadius) {
      throw new Error('companion arrivalRadius must be smaller than standoffRadius');
    }

    const doorwayIds = new Set<string>();
    for (const doorway of config.doorways) {
      if (doorway.id.length === 0 || doorwayIds.has(doorway.id)) throw new Error('companion doorway ids must be unique and non-empty');
      doorwayIds.add(doorway.id);
      finite(doorway.min.x, `doorway ${doorway.id}.min.x`);
      finite(doorway.min.z, `doorway ${doorway.id}.min.z`);
      finite(doorway.max.x, `doorway ${doorway.id}.max.x`);
      finite(doorway.max.z, `doorway ${doorway.id}.max.z`);
      if (doorway.min.x > doorway.max.x || doorway.min.z > doorway.max.z) {
        throw new Error(`companion doorway ${doorway.id} has inverted bounds`);
      }
    }
    // Projection clears only one occupied doorway per fixed step, so room authors
    // must keep volumes disjoint **with a real gap**. Inclusive AABBs that touch
    // are already an overlap for `inside()` and have no safe shared boundary to
    // project to.
    //
    // The gap has to be wider than `DOORWAY_CLEARANCE`, and merely
    // non-overlapping is not enough. An independent review found the case: two
    // doorways 5e-7 apart pass a plain disjointness test, and `#projectOutside`
    // then steps Holly exactly `DOORWAY_CLEARANCE` past the edge of the one she
    // is in — which lands her inside the other, where nothing corrects her
    // because Rosalie is not in that one. If Rosalie later walks into it, the
    // projection pushes her back into the first, and the two volumes trade her
    // back and forth forever.
    //
    // No plausible room places two doorways a micrometre apart. That is exactly
    // why this is a constructor check and not a runtime one: it costs nothing,
    // it fires at authoring time, and it makes the guarantee in
    // `#projectOutside`'s header actually true instead of nearly true.
    const requiredGap = DOORWAY_CLEARANCE * 2;
    for (let firstIndex = 0; firstIndex < config.doorways.length; firstIndex += 1) {
      const first = config.doorways[firstIndex];
      if (first === undefined) continue;
      for (let secondIndex = firstIndex + 1; secondIndex < config.doorways.length; secondIndex += 1) {
        const second = config.doorways[secondIndex];
        if (second === undefined) continue;
        const tooClose = first.min.x - requiredGap <= second.max.x && first.max.x + requiredGap >= second.min.x
          && first.min.z - requiredGap <= second.max.z && first.max.z + requiredGap >= second.min.z;
        if (tooClose) {
          throw new Error(
            `companion doorways ${first.id} and ${second.id} overlap, touch, or are closer than ${requiredGap} m`,
          );
        }
      }
    }

    const stationIds = new Set<string>();
    for (const station of config.stations) {
      if (station.id.length === 0 || stationIds.has(station.id)) throw new Error('companion station ids must be unique and non-empty');
      stationIds.add(station.id);
      finite(station.target.x, `station ${station.id}.target.x`);
      finite(station.target.z, `station ${station.id}.target.z`);
      if (config.doorways.some((doorway) => inside(station.target, doorway))) {
        throw new Error(`companion station ${station.id} is inside a doorway`);
      }
    }

    const obstacleIds = new Set<string>();
    for (const obstacle of config.obstacles) {
      if (obstacle.id.length === 0 || obstacleIds.has(obstacle.id)) throw new Error('companion obstacle ids must be unique and non-empty');
      obstacleIds.add(obstacle.id);
      finite(obstacle.min.x, `obstacle ${obstacle.id}.min.x`);
      finite(obstacle.min.z, `obstacle ${obstacle.id}.min.z`);
      finite(obstacle.max.x, `obstacle ${obstacle.id}.max.x`);
      finite(obstacle.max.z, `obstacle ${obstacle.id}.max.z`);
      if (obstacle.min.x > obstacle.max.x || obstacle.min.z > obstacle.max.z) {
        throw new Error(`companion obstacle ${obstacle.id} has inverted bounds`);
      }
    }
    // Unlike doorway projections, overlapping/touching obstacles are ordinary:
    // a room shell is built from overlapping boxes. Do not copy the doorway-gap
    // check here, or a valid wall would fail at boot.

    const inflated = (obstacle: CompanionObstacle): CompanionObstacle => ({
      id: obstacle.id,
      min: { x: obstacle.min.x - config.bodyRadius, z: obstacle.min.z - config.bodyRadius },
      max: { x: obstacle.max.x + config.bodyRadius, z: obstacle.max.z + config.bodyRadius },
    });
    for (const station of config.stations) {
      const blocking = config.obstacles.find((obstacle) => inside(station.target, inflated(obstacle)));
      // A station inside any possible obstacle is one Holly can never arrive at.
      // Beat 9 ends on exactly that arrival, so validate every authored obstacle
      // now rather than relying on a per-step active subset and silently softlocking.
      if (blocking !== undefined) throw new Error(`companion station ${station.id} is inside obstacle ${blocking.id}`);
    }

    const initial: MutableState = {
      x: config.initial.x,
      z: config.initial.z,
      yaw: normaliseAngle(config.initial.yaw),
      moving: config.initial.moving ?? false,
      phase: config.initial.phase,
      travelledDistance: config.initial.travelledDistance ?? 0,
    };
    if (config.doorways.some((doorway) => inside(initial, doorway))) {
      throw new Error('companion initial position must be outside doorways');
    }
    if (config.obstacles.some((obstacle) => inside(initial, inflated(obstacle)))) {
      throw new Error('companion initial position must be outside inflated obstacles');
    }
    // A non-following initial state cannot visibly be moving; accepting it would
    // make a reset create a bell-distance change without a legal simulation step.
    if (initial.phase !== 'following') initial.moving = false;
    this.#config = config;
    this.#initial = { ...initial };
    this.#state = { ...initial };
  }

  /** A copy prevents presentation callers from mutating deterministic state. */
  snapshot(): CompanionSnapshot {
    return sameSnapshot(this.#state);
  }

  /** Freeing wire is the only legal entry to the companion relationship. */
  release(): boolean {
    if (this.#state.phase !== 'trapped') return false;
    this.#state.phase = 'released';
    this.#state.moving = false;
    return true;
  }

  /** Begin ordinary following only after the wire has been released. */
  beginFollowing(): boolean {
    if (this.#state.phase !== 'released') return false;
    this.#state.phase = 'following';
    return true;
  }

  /** Holly may approach the high latch only while following Rosalie's lead. */
  beginHighLatch(): boolean {
    if (this.#state.phase !== 'following') return false;
    this.#state.phase = 'at-high-latch';
    this.#state.moving = false;
    return true;
  }

  /**
   * Turn her to an authored heading. Legal only while she is standing still.
   *
   * **Added because the first frame of beat 9's climax had her reaching for the
   * bar side-on.** `#moveToward` sets yaw from the direction she travelled, and
   * on arrival at a station that is the direction she happened to come from —
   * which for a companion who has just walked around the player can be anything.
   * She reached the right height, in the right place, facing the wrong wall, and
   * **no gate could have caught it**: the reach is measured in metres of muzzle
   * height, and height does not depend on yaw.
   *
   * Refused while `following` on purpose. A heading written during travel would
   * be overwritten by the next step's own yaw, so a caller doing that has a bug
   * whether or not this method lets them express it.
   */
  face(yaw: number): boolean {
    finite(yaw, 'face yaw');
    if (this.#state.phase === 'following') return false;
    this.#state.yaw = normaliseAngle(yaw);
    return true;
  }

  /** Opening is a separate explicit puzzle state; animation cannot promote it. */
  beginOpeningDoor(): boolean {
    if (this.#state.phase !== 'at-high-latch') return false;
    this.#state.phase = 'opening-door';
    return true;
  }

  /**
   * Return from the latch to ordinary following once the bar has lifted. She is
   * stationary 0.59 m in front of the doorway she just opened; without this she
   * remains in the player's way for the rest of the beat. The normal follow
   * standoff steps her aside, so there is deliberately no second "step aside"
   * station.
   */
  resumeFollowing(): boolean {
    if (this.#state.phase !== 'opening-door') return false;
    this.#state.phase = 'following';
    return true;
  }

  /**
   * Settle into a rest state, which is legal only once the door action is done.
   *
   * **Beat 9's rest shed does not use this**, and the comment saying it did
   * would have been the kind of confidently wrong one D-097 is about. That room
   * is a separate authored part with no `Companion` in it at all — Holly is a
   * frozen snapshot, because a controller that could move her is the one thing
   * the scene must not contain (`woolShedRest.ts`). This stays because it is the
   * transition a companion who rests *in the room she walked into* needs, which
   * is beat 10's problem, and `companion.spec.ts` covers both its verdicts.
   */
  beginRest(): boolean {
    if (this.#state.phase !== 'opening-door') return false;
    this.#state.phase = 'resting';
    this.#state.moving = false;
    return true;
  }

  /** Restore exactly the supplied authored state, including accumulated distance. */
  reset(): void {
    Object.assign(this.#state, this.#initial);
  }

  /**
   * Advance one fixed simulation step. Invalid input is rejected before a write,
   * so a bad caller cannot poison a later valid tick with NaN coordinates.
   */
  step(dt: number, rosalia: Vec2, context: CompanionContext): CompanionSnapshot {
    finite(dt, 'step dt');
    if (dt < 0) throw new Error('companion step dt must not be negative');
    finite(rosalia.x, 'rosalia.x');
    finite(rosalia.z, 'rosalia.z');
    if (typeof context.atStation !== 'boolean') throw new Error('companion context atStation must be boolean');
    if (context.stationIds !== undefined) {
      for (const id of context.stationIds) {
        if (typeof id !== 'string') throw new Error('companion context stationIds must contain strings');
        // **A typo here used to be a softlock with no symptom.** An id the config
        // does not know was silently skipped, `#targetFor` found no candidate,
        // returned null, and Holly simply stopped — no error, no flag, nothing in
        // the console. In a room whose whole beat is "the companion follows you",
        // that presents as the companion being broken, three layers away from the
        // one character of level data that caused it. An independent review found
        // it before any room consumed this.
        //
        // An EMPTY list is still legal and still means "hold": that is a real
        // authored state (no station is usable this step), not a mistake.
        if (!this.#config.stations.some((station) => station.id === id)) {
          throw new Error(`companion context stationIds names unknown station "${id}"`);
        }
      }
    }
    if (context.obstacleIds !== undefined) {
      for (const id of context.obstacleIds) {
        if (typeof id !== 'string') throw new Error('companion context obstacleIds must contain strings');
        // An unknown active id otherwise reads as a harmless empty filter, which
        // lets a misspelled room solid remove collision for exactly one replay.
        // Empty remains legal: a pallet in flight is a real no-solid state.
        if (!this.#config.obstacles.some((obstacle) => obstacle.id === id)) {
          throw new Error(`companion context obstacleIds names unknown obstacle "${id}"`);
        }
      }
    }

    this.#state.moving = false;
    if (dt === 0 || this.#state.phase !== 'following') return this.snapshot();

    const occupied = this.#config.doorways.filter((doorway) => inside(rosalia, doorway));
    if (occupied.length > 0) {
      const containingHolly = occupied.find((doorway) => inside(this.#state, doorway));
      if (containingHolly !== undefined) this.#projectOutside(containingHolly, rosalia);
      // If Holly was already outside, holding is the only non-blocking response.
      return this.snapshot();
    }

    const active = context.obstacleIds === undefined
      ? this.#config.obstacles
      : this.#config.obstacles.filter((obstacle) => context.obstacleIds?.includes(obstacle.id));
    this.#resolveObstacles(active);

    const target = this.#targetFor(rosalia, context);
    if (target === null) return this.snapshot();
    this.#moveToward(target, dt, this.#config.arrivalRadius, active);
    return this.snapshot();
  }

  #targetFor(rosalia: Vec2, context: CompanionContext): Vec2 | null {
    if (context.atStation) {
      const allowed = context.stationIds === undefined ? null : new Set(context.stationIds);
      let best: CompanionStation | null = null;
      let bestDistance = Number.POSITIVE_INFINITY;
      for (const station of this.#config.stations) {
        if (allowed !== null && !allowed.has(station.id)) continue;
        const distance = Math.hypot(station.target.x - this.#state.x, station.target.z - this.#state.z);
        // Strictly less retains authored array order for an exact tie.
        if (distance < bestDistance) {
          best = station;
          bestDistance = distance;
        }
      }
      return best?.target ?? null;
    }

    const dx = this.#state.x - rosalia.x;
    const dz = this.#state.z - rosalia.z;
    const distance = Math.hypot(dx, dz);
    if (distance > EPSILON) {
      return {
        x: rosalia.x + dx / distance * this.#config.standoffRadius,
        z: rosalia.z + dz / distance * this.#config.standoffRadius,
      };
    }
    // If a reset/authored placement puts both at one point, use Holly's current
    // facing to choose a deterministic side rather than a random perpendicular.
    return {
      x: rosalia.x - Math.sin(this.#state.yaw) * this.#config.standoffRadius,
      z: rosalia.z - Math.cos(this.#state.yaw) * this.#config.standoffRadius,
    };
  }

  #moveToward(target: Vec2, dt: number, arrivalRadius: number, obstacles: readonly CompanionObstacle[]): void {
    finite(target.x, 'target.x');
    finite(target.z, 'target.z');
    const dx = target.x - this.#state.x;
    const dz = target.z - this.#state.z;
    const distance = Math.hypot(dx, dz);
    if (distance <= arrivalRadius + EPSILON) return;

    const move = Math.min(
      distance - arrivalRadius,
      this.#integratedMove(distance, dt),
    );
    if (move <= EPSILON) return;
    const delta = { x: dx / distance * move, z: dz / distance * move };
    // Axis order is deterministic, not physical. A wall slide therefore cannot
    // be perfectly frame-rate-independent as unobstructed travel is, but every
    // replay makes the same authored choice rather than depending on box order.
    const candidates = [delta, { x: delta.x, z: 0 }, { x: 0, z: delta.z }];
    const accepted = candidates.find((candidate) => !this.#insideObstacle({
      x: this.#state.x + candidate.x,
      z: this.#state.z + candidate.z,
    }, obstacles));
    if (accepted === undefined) return;
    const actual = Math.hypot(accepted.x, accepted.z);
    if (actual <= EPSILON) return;
    this.#state.x += accepted.x;
    this.#state.z += accepted.z;
    this.#state.yaw = normaliseAngle(Math.atan2(accepted.x, accepted.z));
    this.#state.travelledDistance += actual;
    this.#state.moving = true;
  }

  #insideObstacle(point: Vec2, obstacles: readonly CompanionObstacle[]): boolean {
    return obstacles.some((obstacle) => inside(point, {
      min: { x: obstacle.min.x - this.#config.bodyRadius, z: obstacle.min.z - this.#config.bodyRadius },
      max: { x: obstacle.max.x + this.#config.bodyRadius, z: obstacle.max.z + this.#config.bodyRadius },
    }));
  }

  /**
   * Constraint projection, like `#projectOutside`: this exists for the tipped
   * pallet's footprint appearing under Holly, so **the correction itself** is
   * not travel, does not turn her, and never lets the bell read a shove as a
   * step.
   *
   * It does NOT mean the tick produces no travel. `step()` runs this and then
   * `#moveToward`, and a walk that is legal from the corrected position counts
   * in full — as it should, because she really did walk it. An independent
   * review read the older wording as promising the stronger thing, which is
   * fair: it was a comment claiming a property the code does not have, and this
   * repo has already paid for one of those (D-097). The narrow claim is the true
   * one and it is the one the bell depends on.
   *
   * Candidates that land inside another active obstacle are rejected first, the
   * same way `#projectOutside` refuses to step out of one doorway into another —
   * without it a push out of a wall could deposit her inside the bales, which
   * overlapping shell boxes make an ordinary arrangement rather than a bad one.
   * Eight passes bound the rest; a room needing more has a data problem a
   * runtime loop cannot fix.
   */
  #resolveObstacles(obstacles: readonly CompanionObstacle[]): void {
    for (let pass = 0; pass < 8; pass += 1) {
      const obstacle = obstacles.find((candidate) => this.#insideObstacle(this.#state, [candidate]));
      if (obstacle === undefined) return;
      const minX = obstacle.min.x - this.#config.bodyRadius;
      const maxX = obstacle.max.x + this.#config.bodyRadius;
      const minZ = obstacle.min.z - this.#config.bodyRadius;
      const maxZ = obstacle.max.z + this.#config.bodyRadius;
      const candidates: readonly Vec2[] = [
        { x: minX - OBSTACLE_CLEARANCE, z: this.#state.z }, { x: maxX + OBSTACLE_CLEARANCE, z: this.#state.z },
        { x: this.#state.x, z: minZ - OBSTACLE_CLEARANCE }, { x: this.#state.x, z: maxZ + OBSTACLE_CLEARANCE },
      ];
      const clear = candidates.filter((candidate) => !obstacles.some((other) => (
        other.id !== obstacle.id && this.#insideObstacle(candidate, [other])
      )));
      const choices = clear.length > 0 ? clear : candidates;
      let best = choices[0];
      if (best === undefined) throw new Error('companion obstacle exit candidates missing');
      let bestDistance = Math.hypot(best.x - this.#state.x, best.z - this.#state.z);
      for (const candidate of choices.slice(1)) {
        const distance = Math.hypot(candidate.x - this.#state.x, candidate.z - this.#state.z);
        // Strictly less holds the documented west, east, north, south tie order.
        if (distance < bestDistance) { best = candidate; bestDistance = distance; }
      }
      this.#state.x = best.x;
      this.#state.z = best.z;
    }
  }

  /**
   * This is a constraint projection, not companion travel: a collision overlap
   * must be resolved on the step it becomes known, before it can block Rosalie.
   * It therefore does not increment distance, turn yaw or mark movement (and a
   * future bell seam cannot mistake the correction for walking). Presentation
   * may smooth the visible correction while simulation remains safe. Fixed side
   * order makes the centre deterministic: west, east, north, south. Candidates
   * which do not strictly increase separation from Rosalie are excluded, so
   * resolving overlap cannot shove her across the doorway or sidestep in place.
   * Coincident positions or numerical exhaustion fall back to that documented
   * order rather than inventing a random side.
   */
  #projectOutside(doorway: CompanionDoorway, rosalia: Vec2): void {
    const candidates: readonly Vec2[] = [
      { x: doorway.min.x - DOORWAY_CLEARANCE, z: this.#state.z },
      { x: doorway.max.x + DOORWAY_CLEARANCE, z: this.#state.z },
      { x: this.#state.x, z: doorway.min.z - DOORWAY_CLEARANCE },
      { x: this.#state.x, z: doorway.max.z + DOORWAY_CLEARANCE },
    ];
    const awayX = this.#state.x - rosalia.x;
    const awayZ = this.#state.z - rosalia.z;
    const eligible = candidates.filter((candidate) => (
      (candidate.x - this.#state.x) * awayX + (candidate.z - this.#state.z) * awayZ > EPSILON
      // Never step out of one doorway and into another. The constructor's gap
      // check should make this unreachable; it is here as well because the two
      // together are what let the header above state a guarantee rather than an
      // expectation, and because this one survives a future config path that
      // does not go through the constructor.
      && !this.#config.doorways.some((other) => other.id !== doorway.id && inside(candidate, other))
    ));
    const choices = eligible.length > 0 ? eligible : candidates;
    let best = choices[0];
    if (best === undefined) throw new Error('companion exit candidates missing');
    let bestDistance = Math.hypot(best.x - this.#state.x, best.z - this.#state.z);
    for (const candidate of choices.slice(1)) {
      const distance = Math.hypot(candidate.x - this.#state.x, candidate.z - this.#state.z);
      // Strictly less preserves the documented candidate order for equal edges.
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
    finite(best.x, 'doorway projection.x');
    finite(best.z, 'doorway projection.z');
    this.#state.x = best.x;
    this.#state.z = best.z;
    this.#state.moving = false;
  }

  /**
   * Exact capped-exponential travel: one step of `dt` equals N steps of `dt/N`
   * toward a static target, to float precision.
   *
   * The integration itself composes exactly. The **caller** does not, quite:
   * `#moveToward` discards any single-step move below `EPSILON`, so subdividing a
   * tick into billions of substeps would lose travel to that dead band. At 60 Hz,
   * or at any rate a sim loop will ever run, it cannot be reached. Recorded
   * rather than fixed because the dead band is what stops a target Holly has
   * already arrived at from accumulating bell distance forever.
   */
  #integratedMove(distance: number, dt: number): number {
    const threshold = this.#config.moveSpeed / this.#config.smoothing;
    if (distance <= threshold) return distance * (1 - Math.exp(-this.#config.smoothing * dt));
    const cappedSeconds = (distance - threshold) / this.#config.moveSpeed;
    if (dt <= cappedSeconds) return this.#config.moveSpeed * dt;
    return distance - threshold * Math.exp(-this.#config.smoothing * (dt - cappedSeconds));
  }
}
